import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, PackagePlus, Sparkles, Wand2, MessageSquare } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PricingSection, MachineDetails, MediaUpload } from "@/components/sell/SellSections";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HUMAN_INTENTS, ENGAGEMENT_TYPES, MACHINE_CONDITIONS, optLabel, BLUEPRINT_PRIMARIES } from "@/lib/listingDyn";
import { LANGUAGES, DELIVERY_CHANNELS, PRICING_TYPES, CURRENCIES } from "@/lib/serviceFields";
import { toast } from "sonner";

const emptySpecs = { motor_power_kw: "", spindle_diameter_mm: "", weight_kg: "", max_rpm: "", year: "" };
const emptyDyn = {
  working_width_mm: "", knives_count: "", motor_power_kw: "", shaft_thickness_mm: "", machine_condition: "",
  capacity_m3: "", gate_dimensions: "", package_space_dimensions: "", heating_medium: "", controller_type: "", insulation_type: "",
  craft_type: "", is_vetted: false, human_intent: "", engagement_type: "",
};

const emptySvc = {
  languages: [], delivery_channels: [],
  base_city: "", radius_km: "", national: false, international: false,
  pricing_type: "", rate: "", currency: "RON", min_budget: "", max_budget: "",
};

export default function SellMachine() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lng = (i18n.language || "ro").slice(0, 2);
  const [cats, setCats] = useState([]);
  const [premiumLoading, setPremiumLoading] = useState(false);
  const [counties, setCounties] = useState([]);
  const [cities, setCities] = useState({});
  const [rawText, setRawText] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [form, setForm] = useState({
    title: "", intent: "oferta", mk_subcategory: "", mk_primary: "", condition: "second-hand",
    price: "", price_on_request: false, currency: "EUR", price_type: "fixed", price_min: "", price_max: "",
    description: "", main_parameters: "",
    county: "", location: "", seller_phone: "", specs: { ...emptySpecs },
    is_anonymous: false, company_name: "", investment_min: "", investment_max: "",
    is_social_impact: false, association_id: "",
    dyn: { ...emptyDyn },
    svc: { ...emptySvc },
  });
  const [associations, setAssociations] = useState([]);
  const [images, setImages] = useState([]); // {path,url}
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/autentificare");
  }, [loading, user, navigate]);

  useEffect(() => {
    api.get("/marketplace-taxonomy").then((r) => setCats(r.data.flat)).catch(() => {});
    api.get("/ro-locations").then((r) => { setCounties(r.data.counties); setCities(r.data.cities); }).catch(() => {});
    api.get("/associations").then((r) => setAssociations(r.data)).catch(() => {});
  }, []);

  const selectedCat = cats.find((c) => c.id === form.mk_subcategory);
  const kind = selectedCat?.kind || aiResult?.kind || "machine";
  const isInvestment = kind === "investment" || ["caut_investor", "vreau_sa_investesc"].includes(form.intent);
  const primaryId = form.mk_primary;
  const hasBlueprint = BLUEPRINT_PRIMARIES.includes(primaryId);
  const isSecondary = primaryId === "utilaje-secundare";
  const isEnergy = primaryId === "uscare-termice";
  const isOrphan = primaryId === "mestesuguri-traditii";
  const isService = kind === "service";

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setSpec = (k) => (e) => setForm({ ...form, specs: { ...form.specs, [k]: e.target.value } });
  const setDyn = (k) => (e) => setForm({ ...form, dyn: { ...form.dyn, [k]: e.target.value } });
  const setDynVal = (k, v) => setForm({ ...form, dyn: { ...form.dyn, [k]: v } });
  const setSvc = (k) => (e) => setForm({ ...form, svc: { ...form.svc, [k]: e.target.value } });
  const setSvcVal = (k, v) => setForm({ ...form, svc: { ...form.svc, [k]: v } });
  const toggleSvcArr = (k, v) => setForm((f) => {
    const arr = f.svc[k] || [];
    return { ...f, svc: { ...f.svc, [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] } };
  });

  const buyPremiumReport = async () => {
    setPremiumLoading(true);
    try {
      const { data } = await api.post("/valuation/premium-checkout", { text: rawText, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Eroare la inițierea plății.");
      setPremiumLoading(false);
    }
  };

  const analyze = async () => {
    if (rawText.trim().length < 10) return toast.error("Lipește mai mult text (ex: anunțul din WhatsApp).");
    setAnalyzing(true);
    try {
      const r = await api.post("/listings/classify", { text: rawText });
      const d = r.data;
      setAiResult(d);
      const specs = { ...emptySpecs };
      for (const [k, v] of Object.entries(d.specs || {})) if (v != null) specs[k] = String(v);
      setForm((f) => ({
        ...f,
        title: d.suggested_title || f.title,
        intent: d.intent || f.intent,
        mk_subcategory: d.subcategory_id || f.mk_subcategory,
        mk_primary: d.primary_id || f.mk_primary,
        condition: d.condition || f.condition,
        price: d.price != null ? String(d.price) : f.price,
        county: d.county || f.county,
        location: d.location || f.location,
        description: d.description || f.description,
        specs,
      }));
      if (d.needs_review) toast.warning("Am completat ce am putut — verifică și alege categoria corectă.");
      else toast.success("Formular completat automat! Verifică detaliile înainte de publicare.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Analiza AI a eșuat. Completează manual.");
    } finally {
      setAnalyzing(false);
    }
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/listings/upload", fd);
        setImages((prev) => [...prev, r.data]);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Eroare la încărcarea imaginii");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (path) => setImages((prev) => prev.filter((i) => i.path !== path));

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Adaugă un titlu");
    if (!form.mk_subcategory) return toast.error("Alege o categorie");
    if (!form.seller_phone.trim()) return toast.error("Telefonul de contact este obligatoriu");
    if (!form.county) return toast.error("Alege un județ");
    if (!form.location.trim()) return toast.error("Localitatea este obligatorie");
    if (!form.price_on_request) {
      if (form.price_type === "range") {
        if (!form.price_min || !form.price_max || Number(form.price_min) <= 0 || Number(form.price_max) <= 0)
          return toast.error("Completează intervalul de preț (de la / până la)");
      } else if (!form.price || Number(form.price) <= 0) {
        return toast.error("Adaugă un preț sau bifează „Preț la cerere”");
      }
    }
    setSaving(true);
    const specs = {};
    for (const [k, v] of Object.entries(form.specs)) if (v !== "" && v != null) specs[k] = Number(v);
    const d = form.dyn;
    const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
    const dynPayload = {};
    if (isSecondary) dynPayload.prelucrare_secundara = {
      working_width_mm: numOrNull(d.working_width_mm), knives_count: numOrNull(d.knives_count),
      motor_power_kw: numOrNull(d.motor_power_kw), shaft_thickness_mm: numOrNull(d.shaft_thickness_mm),
      machine_condition: d.machine_condition || null,
    };
    else if (isEnergy) dynPayload.energie_incalzire = {
      capacity_m3: numOrNull(d.capacity_m3), gate_dimensions: d.gate_dimensions || null,
      package_space_dimensions: d.package_space_dimensions || null, heating_medium: d.heating_medium || null,
      controller_type: d.controller_type || null, insulation_type: d.insulation_type || null,
    };
    else if (isOrphan) dynPayload.orphan_trades = {
      craft_type: d.craft_type || null, is_vetted: !!d.is_vetted,
      human_intent: d.human_intent || null, engagement_type: d.engagement_type || null,
    };
    if (isService) {
      const s = form.svc;
      dynPayload.service_details = {
        languages: s.languages, delivery_channels: s.delivery_channels,
        coverage: {
          base_city: s.base_city || null, radius_km: numOrNull(s.radius_km),
          national: !!s.national, international: !!s.international,
        },
        pricing: {
          pricing_type: s.pricing_type || null, rate: numOrNull(s.rate),
          currency: s.currency || "RON", min_budget: numOrNull(s.min_budget), max_budget: numOrNull(s.max_budget),
        },
      };
    }
    const payload = {
      title: form.title, intent: form.intent,
      mk_subcategory: form.mk_subcategory, mk_primary: form.mk_primary,
      condition: form.condition,
      currency: form.currency,
      price: form.price_on_request || form.price_type === "range" ? null : (form.price === "" ? null : Number(form.price)),
      price_min: !form.price_on_request && form.price_type === "range" && form.price_min !== "" ? Number(form.price_min) : null,
      price_max: !form.price_on_request && form.price_type === "range" && form.price_max !== "" ? Number(form.price_max) : null,
      price_on_request: form.price_on_request, description: form.description,
      main_parameters: form.main_parameters, county: form.county, location: form.location,
      seller_phone: form.seller_phone, specs, images: images.map((i) => i.path),
      is_anonymous: form.is_anonymous, company_name: form.company_name,
      investment_min: form.investment_min === "" ? null : Number(form.investment_min),
      investment_max: form.investment_max === "" ? null : Number(form.investment_max),
      is_social_impact: form.is_social_impact,
      association_id: form.is_social_impact ? form.association_id : "",
      ...dynPayload,
    };
    try {
      await api.post("/listings", payload);
      toast.success("Anunț trimis! Va fi publicat după aprobarea administratorului.");
      navigate("/anunturile-mele");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 apprentice-form" data-testid="sell-machine-page">
      <div className="flex items-center gap-3 mb-1">
        <PackagePlus className="w-6 h-6 text-orange-600" />
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Adaugă un anunț pe OXO</h1>
      </div>
      <p className="text-slate-500 text-sm mb-5">Vinzi sau cauți utilaje, materiale sau servicii? Completează detaliile — anunțul devine public după aprobarea echipei OXO.</p>

      {/* SMS listing — coming soon */}
      <div className="border-l-4 border-green-600 bg-green-50 px-4 py-3 mb-6 flex items-start gap-3" data-testid="sms-listing-soon">
        <MessageSquare className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-black text-green-700">În curând</div>
          <p className="text-sm text-slate-700 font-semibold leading-snug">Postează anunțul offline, printr-un simplu SMS!</p>
          <p className="text-xs text-slate-500 mt-0.5">Fără internet, direct din atelier — trimiți un SMS și noi publicăm anunțul.</p>
        </div>
      </div>

      {/* AI smart-paste */}
      <div className="border border-orange-200 bg-orange-50/60 p-5 mb-8" data-testid="ai-smart-paste">
        <div className="flex items-center gap-2 mb-1.5">
          <Sparkles className="w-5 h-5 text-orange-600" />
          <h2 className="text-base font-bold text-slate-900">Completare rapidă cu AI</h2>
        </div>
        <p className="text-sm text-slate-600 mb-3">Lipește textul anunțului (ex: dintr-un grup WhatsApp) și AI-ul completează automat titlul, categoria și specificațiile.</p>
        <Textarea
          value={rawText}
          onChange={(e) => setRawText(e.target.value)}
          placeholder="Ex: Vând gater orizontal Wood-Mizer LT40, an 2016, motor 18kW, stare f bună, Suceava, 95000 lei negociabil..."
          className="rounded-none bg-white min-h-[100px]"
          data-testid="ai-raw-text"
        />
        <button
          onClick={analyze}
          disabled={analyzing}
          className="mt-3 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white px-5 py-2.5 font-bold uppercase tracking-wide text-xs transition-colors"
          data-testid="ai-analyze-btn"
        >
          {analyzing ? <><Loader2 className="w-4 h-4 animate-spin" /> Se analizează...</> : <><Wand2 className="w-4 h-4" /> Completează automat</>}
        </button>
        {aiResult && aiResult.subcategory_name && (
          <span className="ml-3 text-sm text-slate-700" data-testid="ai-result-badge">
            Detectat: <b className="text-orange-700">{aiResult.primary_name} → {aiResult.subcategory_name}</b>
            <span className="text-slate-400"> ({Math.round((aiResult.confidence || 0) * 100)}%)</span>
          </span>
        )}

        {aiResult && aiResult.price_estimate && (aiResult.price_estimate.low || aiResult.price_estimate.high) && (
          <div className="mt-4 border border-slate-200 bg-white p-4" data-testid="ai-price-estimate">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-xs uppercase tracking-wide font-bold text-slate-500">{t("valuation.estTitle")} <span className="text-green-600">({t("valuation.free")})</span></div>
                <div className="text-2xl font-black text-slate-900 mt-1">
                  {aiResult.price_estimate.low ? new Intl.NumberFormat("ro-RO").format(aiResult.price_estimate.low) : "—"}
                  {" – "}
                  {aiResult.price_estimate.high ? new Intl.NumberFormat("ro-RO").format(aiResult.price_estimate.high) : "—"} RON
                </div>
                <div className="text-xs text-slate-400 mt-0.5">{t("valuation.estRange")}</div>
              </div>
              <button onClick={buyPremiumReport} disabled={premiumLoading} className="inline-flex flex-col items-start bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-slate-900 px-4 py-2.5 transition-colors" data-testid="premium-report-cta">
                <span className="font-bold text-sm flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> {premiumLoading ? "..." : t("valuation.premiumCta")} · {t("valuation.premiumPrice")}</span>
                <span className="text-[11px] opacity-80">{t("valuation.premiumDesc")}</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-5">
        {/* Intent toggle */}
        <div>
          <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Tip anunț *</Label>
          <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="listing-intent">
            {[
              { v: "oferta", label: t("capital.intentOffer"), sub: t("capital.intentOfferSub") },
              { v: "cerere", label: t("capital.intentRequest"), sub: t("capital.intentRequestSub") },
              { v: "caut_investor", label: t("capital.intentSeekInvestor"), sub: t("capital.intentCapitalSub") },
              { v: "vreau_sa_investesc", label: t("capital.intentInvest"), sub: t("capital.intentCapitalSub") },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => setForm({ ...form, intent: o.v })}
                className={`border px-4 py-3 text-left transition-colors ${form.intent === o.v ? "border-orange-600 bg-orange-50 text-orange-700" : "border-slate-300 text-slate-600 hover:border-slate-400"}`}
                data-testid={`intent-${o.v}`}
              >
                <div className="font-bold text-sm">{o.label}</div>
                <div className="text-xs opacity-70">{o.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* OXO Capital — investment details + blind listing */}
        {isInvestment && (
          <div className="border border-indigo-200 bg-indigo-50/60 p-5 space-y-4" data-testid="investment-block">
            <div className="text-sm font-bold text-indigo-900">{t("capital.investmentDetails")}</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">{t("capital.amountMin")}</Label>
                <Input type="number" value={form.investment_min} onChange={set("investment_min")} placeholder="ex: 200000" className="rounded-none mt-1.5" data-testid="investment-min" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">{t("capital.amountMax")}</Label>
                <Input type="number" value={form.investment_max} onChange={set("investment_max")} placeholder="ex: 500000" className="rounded-none mt-1.5" data-testid="investment-max" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">{t("capital.companyName")}</Label>
              <Input value={form.company_name} onChange={set("company_name")} placeholder={t("capital.companyNamePh")} className="rounded-none mt-1.5" data-testid="company-name" />
            </div>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="anonymous-toggle-label">
              <Checkbox checked={form.is_anonymous} onCheckedChange={(v) => setForm({ ...form, is_anonymous: !!v })} data-testid="listing-anonymous" />
              <span className="text-sm text-slate-700">
                <b>{t("capital.blindTitle")}</b><br />
                <span className="text-slate-500 text-xs">{t("capital.blindDesc")}</span>
              </span>
            </label>
          </div>
        )}

        {/* Social-impact (Árva Szakmák / Orphan Trades) */}
        <div className="border border-emerald-200 bg-emerald-50/60 p-5 space-y-3" data-testid="social-impact-block">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={form.is_social_impact} onCheckedChange={(v) => setForm({ ...form, is_social_impact: !!v })} data-testid="listing-social-impact" />
            <span className="text-sm text-slate-700">
              <b>{t("trades.socialTitle")}</b><br />
              <span className="text-slate-500 text-xs">{t("trades.socialDesc")}</span>
            </span>
          </label>
          {form.is_social_impact && (
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">{t("trades.association")}</Label>
              <Select value={form.association_id} onValueChange={(v) => setForm({ ...form, association_id: v })}>
                <SelectTrigger className="rounded-none mt-1.5" data-testid="association-select"><SelectValue placeholder={t("trades.chooseAssociation")} /></SelectTrigger>
                <SelectContent>
                  {associations.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Titlu anunț *</Label>
          <Input value={form.title} onChange={set("title")} placeholder="Ex: Mașină de frezat SCM 2018" className="rounded-none mt-1.5" data-testid="listing-title" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Categorie *</Label>
            <Select value={form.mk_subcategory} onValueChange={(v) => setForm({ ...form, mk_subcategory: v, mk_primary: cats.find((c) => c.id === v)?.parent || "" })}>
              <SelectTrigger className="rounded-none mt-1.5" data-testid="listing-category"><SelectValue placeholder="Alege categoria" /></SelectTrigger>
              <SelectContent className="max-h-[50vh]">
                {cats.map((c) => (
                  c.depth === 0 ? (
                    <div key={c.id} className="px-2 py-1.5 text-xs font-bold uppercase tracking-wide text-orange-600">{c.name}</div>
                  ) : (
                    <SelectItem key={c.id} value={c.id} className="pl-6 text-slate-700">{c.name}</SelectItem>
                  )
                ))}
              </SelectContent>
            </Select>
          </div>
          {kind === "machine" && (
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Stare</Label>
              <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                <SelectTrigger className="rounded-none mt-1.5" data-testid="listing-condition"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="nou">Nou</SelectItem><SelectItem value="second-hand">Second-hand</SelectItem></SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Dynamic blueprint — Secondary Processing (Prelucrare secundară) */}
        {isSecondary && (
          <div className="border border-sky-200 bg-sky-50/60 p-5 space-y-4" data-testid="blueprint-secondary">
            <div className="text-sm font-bold text-sky-900">{t("dyn.secondaryTitle")}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("dyn.working_width_mm")}</Label>
                <Input type="number" value={form.dyn.working_width_mm} onChange={setDyn("working_width_mm")} className="rounded-none mt-1" data-testid="dyn-working-width" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("dyn.knives_count")}</Label>
                <Input type="number" value={form.dyn.knives_count} onChange={setDyn("knives_count")} className="rounded-none mt-1" data-testid="dyn-knives-count" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("dyn.motor_power_kw")}</Label>
                <Input type="number" value={form.dyn.motor_power_kw} onChange={setDyn("motor_power_kw")} className="rounded-none mt-1" data-testid="dyn-motor-power" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("dyn.shaft_thickness_mm")}</Label>
                <Input type="number" value={form.dyn.shaft_thickness_mm} onChange={setDyn("shaft_thickness_mm")} className="rounded-none mt-1" data-testid="dyn-shaft-thickness" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("dyn.machine_condition")}</Label>
                <Select value={form.dyn.machine_condition} onValueChange={(v) => setDynVal("machine_condition", v)}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="dyn-machine-condition"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {MACHINE_CONDITIONS.map((o) => <SelectItem key={o.value} value={o.value}>{optLabel(o, lng)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Dynamic blueprint — Energy & Heating (Energie & Încălzire) */}
        {isEnergy && (
          <div className="border border-rose-200 bg-rose-50/60 p-5 space-y-4" data-testid="blueprint-energy">
            <div className="text-sm font-bold text-rose-900">{t("dyn.energyTitle")}</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("dyn.capacity_m3")}</Label>
                <Input type="number" value={form.dyn.capacity_m3} onChange={setDyn("capacity_m3")} className="rounded-none mt-1" data-testid="dyn-capacity" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("dyn.gate_dimensions")}</Label>
                <Input value={form.dyn.gate_dimensions} onChange={setDyn("gate_dimensions")} placeholder="ex: 2000 x 2200 mm" className="rounded-none mt-1" data-testid="dyn-gate-dimensions" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("dyn.package_space_dimensions")}</Label>
                <Input value={form.dyn.package_space_dimensions} onChange={setDyn("package_space_dimensions")} placeholder="ex: 6000 x 1800 mm" className="rounded-none mt-1" data-testid="dyn-package-dimensions" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("dyn.heating_medium")}</Label>
                <Input value={form.dyn.heating_medium} onChange={setDyn("heating_medium")} placeholder="ex: Apă caldă / Abur" className="rounded-none mt-1" data-testid="dyn-heating-medium" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("dyn.controller_type")}</Label>
                <Input value={form.dyn.controller_type} onChange={setDyn("controller_type")} placeholder="ex: PLC Siemens" className="rounded-none mt-1" data-testid="dyn-controller-type" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("dyn.insulation_type")}</Label>
                <Input value={form.dyn.insulation_type} onChange={setDyn("insulation_type")} placeholder="ex: Vată minerală" className="rounded-none mt-1" data-testid="dyn-insulation-type" />
              </div>
            </div>
          </div>
        )}

        {/* Dynamic blueprint — Orphan Trades human capital (Árva Szakmák) */}
        {isOrphan && (
          <div className="border border-emerald-300 bg-emerald-50/60 p-5 space-y-4" data-testid="blueprint-orphan">
            <div className="text-sm font-bold text-emerald-900">{t("dyn.orphanTitle")}</div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("dyn.human_intent")}</Label>
              <Select value={form.dyn.human_intent} onValueChange={(v) => setDynVal("human_intent", v)}>
                <SelectTrigger className="rounded-none mt-1" data-testid="dyn-human-intent"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {HUMAN_INTENTS.map((o) => <SelectItem key={o.value} value={o.value}>{optLabel(o, lng)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.dyn.human_intent && form.dyn.human_intent !== "donatie_utilaj" && (
              <div data-testid="engagement-type-wrap">
                <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("dyn.engagement_type")}</Label>
                <Select value={form.dyn.engagement_type} onValueChange={(v) => setDynVal("engagement_type", v)}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="dyn-engagement-type"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {ENGAGEMENT_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{optLabel(o, lng)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("dyn.craft_type")}</Label>
              <Input value={form.dyn.craft_type} onChange={setDyn("craft_type")} placeholder="ex: Dulgherie, Cioplit, Fierărie" className="rounded-none mt-1" data-testid="dyn-craft-type" />
            </div>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="dyn-vetted-label">
              <Checkbox checked={form.dyn.is_vetted} onCheckedChange={(v) => setDynVal("is_vetted", !!v)} data-testid="dyn-is-vetted" />
              <span className="text-sm text-slate-700">
                <b>{t("dyn.is_vetted")}</b><br />
                <span className="text-slate-500 text-xs">{t("dyn.vettedHint")}</span>
              </span>
            </label>
          </div>
        )}

        {/* Dynamic blueprint — Service provider (Experți / Servicii) */}
        {isService && (
          <div className="border border-indigo-200 bg-indigo-50/60 p-5 space-y-5" data-testid="blueprint-service">
            <div className="text-sm font-bold text-indigo-900">{t("svc.title")}</div>

            <div>
              <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("svc.languages")}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {LANGUAGES.map((o) => (
                  <button type="button" key={o.value} onClick={() => toggleSvcArr("languages", o.value)}
                    className={`text-xs font-semibold px-3 py-1.5 border transition-colors ${form.svc.languages.includes(o.value) ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-300 text-slate-600 hover:border-indigo-400"}`}
                    data-testid={`svc-lang-${o.value}`}>{optLabel(o, lng)}</button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("svc.delivery")}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {DELIVERY_CHANNELS.map((o) => (
                  <button type="button" key={o.value} onClick={() => toggleSvcArr("delivery_channels", o.value)}
                    className={`text-xs font-semibold px-3 py-1.5 border transition-colors ${form.svc.delivery_channels.includes(o.value) ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-300 text-slate-600 hover:border-indigo-400"}`}
                    data-testid={`svc-delivery-${o.value}`}>{optLabel(o, lng)}</button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("svc.coverage")}</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <Input value={form.svc.base_city} onChange={setSvc("base_city")} placeholder={t("svc.baseCity")} className="rounded-none" data-testid="svc-base-city" />
                <Input type="number" value={form.svc.radius_km} onChange={setSvc("radius_km")} placeholder={t("svc.radiusKm")} className="rounded-none" data-testid="svc-radius" />
              </div>
              <div className="flex flex-wrap gap-4 mt-3">
                <label className="flex items-center gap-2 cursor-pointer text-sm"><Checkbox checked={form.svc.national} onCheckedChange={(v) => setSvcVal("national", !!v)} data-testid="svc-national" /> {t("svc.national")}</label>
                <label className="flex items-center gap-2 cursor-pointer text-sm"><Checkbox checked={form.svc.international} onCheckedChange={(v) => setSvcVal("international", !!v)} data-testid="svc-international" /> {t("svc.international")}</label>
              </div>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t("svc.pricing")}</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                <Select value={form.svc.pricing_type} onValueChange={(v) => setSvcVal("pricing_type", v)}>
                  <SelectTrigger className="rounded-none" data-testid="svc-pricing-type"><SelectValue placeholder={t("svc.pricingType")} /></SelectTrigger>
                  <SelectContent>{PRICING_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{optLabel(o, lng)}</SelectItem>)}</SelectContent>
                </Select>
                {form.svc.pricing_type && form.svc.pricing_type !== "la_cerere" && (
                  <>
                    <Input type="number" value={form.svc.rate} onChange={setSvc("rate")} placeholder={t("svc.rate")} className="rounded-none" data-testid="svc-rate" />
                    <Select value={form.svc.currency} onValueChange={(v) => setSvcVal("currency", v)}>
                      <SelectTrigger className="rounded-none" data-testid="svc-currency"><SelectValue /></SelectTrigger>
                      <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" value={form.svc.min_budget} onChange={setSvc("min_budget")} placeholder={t("svc.minBudget")} className="rounded-none" data-testid="svc-min" />
                    <Input type="number" value={form.svc.max_budget} onChange={setSvc("max_budget")} placeholder={t("svc.maxBudget")} className="rounded-none" data-testid="svc-max" />
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <PricingSection form={form} set={set} setForm={setForm} />

        <div>
          <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">
            {kind === "service" ? "Detalii serviciu" : "Parametri principali"}
          </Label>
          <Textarea value={form.main_parameters} onChange={set("main_parameters")}
            placeholder={kind === "service" ? "Ex: Specializări, certificări, arie de acoperire, experiență..."
              : kind === "material" ? "Ex: Diametru, esență lemn, volum mc, specie..."
              : "Ex: putere 7.5 kW, masă glisantă 3200mm, 3 faze..."}
            className="rounded-none mt-1.5" data-testid="listing-main-parameters" />
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Descriere</Label>
          <Textarea value={form.description} onChange={set("description")} placeholder="Descrierea detaliată..." className="rounded-none mt-1.5 min-h-[120px]" data-testid="listing-description" />
        </div>

        {/* Mandatory contact & location */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Județ *</Label>
            <Select value={form.county} onValueChange={(v) => setForm({ ...form, county: v, location: "" })}>
              <SelectTrigger className="rounded-none mt-1.5" data-testid="listing-county"><SelectValue placeholder="Alege județul" /></SelectTrigger>
              <SelectContent className="max-h-[50vh]">
                {counties.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Localitate *</Label>
            <Input value={form.location} onChange={set("location")} list="city-options" placeholder="Ex: Cluj-Napoca" className="rounded-none mt-1.5" data-testid="listing-location" />
            <datalist id="city-options">
              {(cities[form.county] || []).map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Telefon contact *</Label>
            <Input value={form.seller_phone} onChange={set("seller_phone")} placeholder="07xx xxx xxx" className="rounded-none mt-1.5" data-testid="listing-phone" />
          </div>
        </div>

        <MachineDetails specs={form.specs} setSpec={setSpec} visible={kind === "machine" && !hasBlueprint} />

        <MediaUpload images={images} onFiles={onFiles} uploading={uploading} removeImage={removeImage} />

        <button onClick={submit} disabled={saving || uploading}
          className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-6 py-4 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="submit-listing">
          {saving ? "Se trimite..." : "Publică anunțul"}
        </button>
      </div>
    </div>
  );
}
